/**
 * runModulePaneMissingClearEffect — cleanup for when the open module pane points
 * at a module that no longer exists.
 *
 * While the pane is open on a module, this validates (after the module list has
 * loaded) that the module still exists. If it has vanished, it arms an 1800ms
 * timer; when the timer fires it clears the pane ONLY if the selected module id
 * is still the same one (functional setState guard against a concurrent switch).
 * It also cancels/clears the timer whenever the pane closes or the module
 * reappears. Timer ref + setters are threaded BY REFERENCE.
 */
import type { MutableRefObject } from 'react';
import type { Module } from '@/types/modules';
import { conversationEvents, dispatchWindowEvent } from '@/lib/conversation-events';

export interface ModulePaneMissingClearDeps {
  activeConversationId: string | null | undefined;
  chatPaneModuleId: string | null;
  isModulePaneVisible: boolean;
  availableModules: Module[];
  selectedChatPaneModule: Module | null;
  modulePaneMissingClearTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  setChatPaneModuleId: (updater: (current: string | null) => string | null) => void;
  setIsModulePaneVisible: (visible: boolean) => void;
  setModuleEmbedAnchorMessageId: (id: string | null) => void;
}

export function runModulePaneMissingClearEffect(deps: ModulePaneMissingClearDeps): void {
  const {
    activeConversationId,
    chatPaneModuleId,
    isModulePaneVisible,
    availableModules,
    selectedChatPaneModule,
    modulePaneMissingClearTimerRef,
    setChatPaneModuleId,
    setIsModulePaneVisible,
    setModuleEmbedAnchorMessageId,
  } = deps;

  if (!activeConversationId || !chatPaneModuleId || !isModulePaneVisible) {
    if (modulePaneMissingClearTimerRef.current) {
      clearTimeout(modulePaneMissingClearTimerRef.current);
      modulePaneMissingClearTimerRef.current = null;
    }
    return;
  }
  // Don't clear during module loading; only validate once modules exist.
  if (availableModules.length === 0) return;

  const moduleStillExists = Boolean(selectedChatPaneModule)
    || availableModules.some((module) => module.id === chatPaneModuleId);
  if (moduleStillExists) {
    if (modulePaneMissingClearTimerRef.current) {
      clearTimeout(modulePaneMissingClearTimerRef.current);
      modulePaneMissingClearTimerRef.current = null;
    }
    return;
  }

  if (modulePaneMissingClearTimerRef.current) return;
  modulePaneMissingClearTimerRef.current = setTimeout(() => {
    modulePaneMissingClearTimerRef.current = null;
    let cleared = false;
    setChatPaneModuleId((currentModuleId) => {
      if (currentModuleId === chatPaneModuleId) {
        cleared = true;
        return null;
      }
      return currentModuleId;
    });
    if (cleared) {
      setIsModulePaneVisible(false);
      setModuleEmbedAnchorMessageId(null);
      // Uniform pane-closed notification (ADR 028). The rail's highlight now
      // clears via the ModulePaneContext write above — this dispatch keeps the
      // missing-clear path emitting the same signal as every other
      // user-visible close (hideModulePane) for external listeners/tests.
      dispatchWindowEvent(conversationEvents.modulePaneClosed);
    }
  }, 1800);
}
